import api from '@api/api'

// 默认分片大小：5MB
const DEFAULT_CHUNK_SIZE = 5 * 1024 * 1024
// 默认并发数
const DEFAULT_CONCURRENT = 3
// 单分片最大重试次数
const DEFAULT_RETRIES = 3

/**
 * 分片上传任务
 *
 * 职责：
 * 1. 将文件切片
 * 2. 调用后端 init 接口初始化上传（获取 uploadId）
 * 3. 查询已上传分片，实现断点续传
 * 4. 并发上传剩余分片，失败自动重试
 * 5. 全部分片完成后调用 merge 合并
 * 6. 支持取消
 *
 * 依赖后端接口（见 src/api/api.js 第 206-240 行）：
 *   - uploadChunkInit     {fileName, totalChunks, totalSize} -> {uploadId}
 *   - uploadChunk         {uploadId, chunkIndex, chunk}       -> void
 *   - uploadChunkMerge    {uploadId}                           -> {url, name, ...}
 *   - uploadChunkCancel   {uploadId}                           -> void
 *   - getChunkUploaded    {uploadId}                           -> {uploadedChunks: number[]}
 */
export class ChunkUploadTask {
    /**
     * @param {File} file 待上传的文件
     * @param {Object} options 配置
     * @param {number} [options.chunkSize] 分片大小（字节），默认 5MB
     * @param {number} [options.concurrent] 并发数，默认 3
     * @param {number} [options.retries] 每个分片最大重试次数，默认 3
     * @param {(percent:number)=>void} [options.onProgress] 整体上传进度回调（0-100）
     * @param {(info:{name:string,url:string})=>void} [options.onSuccess] 上传成功回调
     * @param {(err:Error)=>void} [options.onError] 上传失败回调
     */
    constructor(file, options = {}) {
        this.file = file
        this.chunkSize = options.chunkSize || DEFAULT_CHUNK_SIZE
        this.concurrent = options.concurrent || DEFAULT_CONCURRENT
        this.retries = options.retries || DEFAULT_RETRIES
        this.onProgress = options.onProgress || (() => {})
        this.onSuccess = options.onSuccess || (() => {})
        this.onError = options.onError || (() => {})

        // 运行时状态
        this.uploadId = ''          // 后端返回的上传任务 ID
        this.chunks = []            // 切好的分片数组（Blob）
        this.totalChunks = 0        // 分片总数
        this.uploadedSet = new Set()// 已上传完成的分片下标集合
        this._aborted = false       // 是否已取消
    }

    /**
     * 启动上传流程。返回最终后端 merge 接口的数据（包含 url）。
     */
    async start() {
        try {
            // 1. 切片
            this.chunks = this._sliceFile()
            this.totalChunks = this.chunks.length

            // 2. 初始化上传，获取 uploadId
            const initRes = await api.uploadChunkInit({
                fileName: this.file.name,
                totalChunks: this.totalChunks,
                totalSize: this.file.size,
            })
            if (initRes.code !== 200 && initRes.code !== 0) {
                throw new Error(initRes.message || '初始化分片上传失败')
            }
            // 兼容后端返回 {uploadId:xxx} 或直接返回字符串
            this.uploadId = initRes.data?.uploadId || initRes.data

            // 3. 查询已上传分片（断点续传）
            try {
                const uploadedRes = await api.getChunkUploaded({ uploadId: this.uploadId })
                const uploaded = uploadedRes.data?.uploadedChunks || uploadedRes.data || []
                this.uploadedSet = new Set(uploaded.map(Number))
            } catch (err) {
                // 查询失败不影响上传流程，从头上传即可
                console.warn('查询已上传分片失败，将从头上传', err)
                this.uploadedSet = new Set()
            }

            // 先发一次进度（断点续传可能已有完成的分片）
            this._emitProgress()

            // 4. 并发上传剩余分片
            await this._uploadPending()

            // 被取消则直接返回
            if (this._aborted) return

            // 5. 合并分片
            const mergeRes = await api.uploadChunkMerge({ uploadId: this.uploadId })
            if (mergeRes.code !== 200 && mergeRes.code !== 0) {
                throw new Error(mergeRes.message || '合并分片失败')
            }

            this._emitProgress(100)
            this.onSuccess(mergeRes.data)
            return mergeRes.data
        } catch (err) {
            if (this._aborted) return
            this.onError(err)
            throw err
        }
    }

    /**
     * 取消上传：停止派发新请求，并通知后端释放临时文件
     */
    async cancel() {
        if (this._aborted) return
        this._aborted = true
        if (this.uploadId) {
            try {
                await api.uploadChunkCancel({ uploadId: this.uploadId })
            } catch (err) {
                console.warn('通知后端取消上传失败', err)
            }
        }
    }

    /* ---------- 内部方法 ---------- */

    /**
     * 按 chunkSize 切片
     */
    _sliceFile() {
        const chunks = []
        let offset = 0
        while (offset < this.file.size) {
            chunks.push(this.file.slice(offset, offset + this.chunkSize))
            offset += this.chunkSize
        }
        return chunks
    }

    /**
     * 计算并派发当前整体进度
     */
    _emitProgress(percent) {
        if (typeof percent === 'number') {
            this.onProgress(percent)
            return
        }
        const p = this.totalChunks === 0
            ? 0
            : Math.round((this.uploadedSet.size / this.totalChunks) * 100)
        // 最终 100% 留给 merge 成功后统一派发，避免未合并就显示 100%
        this.onProgress(Math.min(p, 99))
    }

    /**
     * 并发上传所有未完成的分片（滑动窗口模式）
     */
    _uploadPending() {
        // 过滤出未完成的分片下标
        const pending = []
        for (let i = 0; i < this.totalChunks; i++) {
            if (!this.uploadedSet.has(i)) pending.push(i)
        }
        if (pending.length === 0) return Promise.resolve()

        return new Promise((resolve, reject) => {
            let running = 0   // 当前进行中的请求数
            let cursor = 0    // 下一个待上传的 pending 元素下标
            let hasError = false

            const scheduleNext = () => {
                if (hasError || this._aborted) return
                // 全部已调度 & 没有在跑的 -> 完成
                if (cursor >= pending.length && running === 0) {
                    resolve()
                    return
                }
                // 在并发上限内，持续派发新任务
                while (running < this.concurrent && cursor < pending.length) {
                    const chunkIndex = pending[cursor++]
                    running++
                    this._uploadSingleWithRetry(chunkIndex)
                        .then(() => {
                            running--
                            this.uploadedSet.add(chunkIndex)
                            this._emitProgress()
                            scheduleNext()
                        })
                        .catch((err) => {
                            if (!hasError) {
                                hasError = true
                                reject(err)
                            }
                        })
                }
            }

            scheduleNext()
        })
    }

    /**
     * 上传单个分片，失败自动重试
     */
    async _uploadSingleWithRetry(chunkIndex) {
        let lastError
        for (let attempt = 0; attempt < this.retries; attempt++) {
            if (this._aborted) throw new Error('已取消')
            try {
                // 使用 FormData 传输二进制分片
                const formData = new FormData()
                formData.append('uploadId', this.uploadId)
                formData.append('chunkIndex', chunkIndex)
                formData.append('chunk', this.chunks[chunkIndex])
                const res = await api.uploadChunk(formData)
                if (res.code !== 200 && res.code !== 0) {
                    throw new Error(res.message || `分片 ${chunkIndex} 上传失败`)
                }
                return
            } catch (err) {
                lastError = err
                if (this._aborted) throw err
            }
        }
        throw lastError || new Error(`分片 ${chunkIndex} 重试后仍失败`)
    }
}

/**
 * 便捷函数：直接上传一个文件，返回 Promise<{url, name, ...}>
 *
 * @param {File} file
 * @param {Object} options 参见 ChunkUploadTask 构造参数
 * @returns {Promise<{url:string, name:string}>} 同时返回上传任务实例（用于取消）
 */
export function uploadFileInChunks(file, options = {}) {
    const task = new ChunkUploadTask(file, options)
    const promise = task.start()
    // 将 task 挂到 promise 上，外部可通过 promise.task.cancel() 取消
    promise.task = task
    return promise
}

export default ChunkUploadTask
